<template>
  <div
    v-click-outside="handleClickOutside"
    class="search-container"
  >
    <img
      class="search-container__icon-search"
      src="/static/images/icon-search-small.svg"
      alt="busca"
    >
    <input
      id="txtNewSearchExam"
      v-model="query"
      type="text"
      name="input-search"
      :placeholder="placeholderCustom"
      maxlength="500"
      class="search__input"
      autocomplete="off"
      @focus="openExamsListDefault()"
      @blur="cleanExamsListDefaultLoaded()"
      @keyup.enter="hit(items[current])"
      @keyup.esc="cleanExamsListDefaultLoaded()"
      @keydown.up="keyUp()"
      @keydown.down="keyDown()"
    >
    <spinner
      v-if="loading"
      class="small search__loading"
    />
    <div
      v-if="!outsideClick"
      id="resultsContainer"
      class="search__results-container"
    >
      <template v-if="!loading">
        <div
          v-for="(item, index) in items"
          :key="index"
          @mouseover="mouseOver(index)"
        >
          <component
            :is="currentSearchComponent"
            :item="item"
            :search="query"
            :is-active="index === current"
            @click.native="hit(item)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Spinner from '../common/Spinner'
import { clickOutside } from '@/utils/directives'
export default {
  name: 'Search',
  components: {
    'spinner': Spinner
  },
  directives: {
    'click-outside': {
      bind: function (el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
          // here I check that click was outside the el and his children
          if (!(el === event.target || el.contains(event.target))) {
            // and if it did, call method provided in attribute value
            vnode.context[binding.expression](event)
          }
        }
        document.body.addEventListener('click', el.clickOutsideEvent)
      },
      unbind: function (el) {
        document.body.removeEventListener('click', el.clickOutsideEvent)
      }
    },
    clickOutside
  },
  props: {
    debounce: {
      type: Number,
      default: 1E3
    },
    fetch: {
      type: Function
    },
    minChars: {
      type: Number,
      default: 1
    },
    placeholder: {
      type: String
    },
    onlyGetExamId: {
      type: Boolean,
      default: false
    }
  },
  data: () => {
    return {
      outsideClick: false,
      limit: 50,                                  // Number of items to show in results list after search.
      items: [],
      query: '',
      selected: null,
      timeout: 0,
      parentContainer: null,
      searchButton: null,
      currentSearchComponent: 'exam-search-item',
      loading: false,
      open: false,
      searchListRef: null,
      current: 0,
      minView: 0,
      maxView: 160,
      placeholderCustom: ''
    }
  },
  watch: {
    query: function (newVal) {
      this.removeChar()
      this.debounceUpdate()
      this.current = 0
    }
  },
  mounted() {
    this.searchListRef = document.getElementById('resultsContainer')
  },
  methods: {
    ...mapActions(['getExamDetails']),
    ...mapActions(['pushLoadingExam']),
    handleClickOutside() {
      this.outsideClick = true
    },
    debounceUpdate() {
      this.items = []
      if (this.timeout) {
        window.clearTimeout(this.timeout)
      }
      if (this.query.length >= this.minChars) {
        this.loading = true
      }

      this.timeout = window.setTimeout(() => {
        this.makeFetchRequest(false)
      }, this.debounce)
    },
    removeChar() {
      var regex = new RegExp('[^0-9a-zA-ZÁ-Ź ]', 'g')
      this.query = this.query.replace(regex, '')
    },
    openExamsListDefault() {
      this.outsideClick = false
      if (this.query === '') {
        this.open = true
        this.current = 0
        this.makeFetchRequest(true)
        this.focusOnElement()
      }
    },
    cleanExamsListDefaultLoaded() {
      new Promise((resolve) => setTimeout(resolve, 200)).then(() => {
        this.reset()
      })
    },
    hit(item) {
      if (!this.onlyGetExamId) {
        if (item && item.id) {
          this.pushLoadingExam(item.id)
          this.getExamDetails(item.id)
          this.reset()
        }
      } else {
        this.$emit('exam-id-selected', item.id)
        this.placeholderCustom = item.name
        this.reset()
      }
    },
    makeFetchRequest(showDefaultList) {
      this.query = this.query.trim()
      return this.fetch({ query: this.query, showDefaultList: showDefaultList })
        .then((response) => {
          let data = response.value
          if (data) {
            this.items = this.limit ? data.slice(0, this.limit) : data
          } else {
            this.items = data
          }
          this.loading = false
          this.getMinAndMaxView()
        })
    },
    reset() {
      this.items = []
      this.query = ''
      this.open = false
      this.current = 0
      this.count = 0
      if (!this.onlyGetExamId && document.getElementById('resultsContainer')) {
        document.getElementById('resultsContainer').scrollTop = 0
      }
    },
    keyUp() {
      if (this.current > 0) {
        this.current--
        if (this.isItemNotVisible()) {
          this.makeVisible()
        }
      }
    },
    keyDown() {
      if (!this.items || this.items.length === 0) {
        this.openExamsListDefault()
      } else if (this.current < this.items.length - 1) {
        this.current++
        if (this.isItemNotVisible()) {
          this.makeVisible()
        }
      }
    },
    isItemNotVisible() {
      return ((this.current * this.searchListRef.offsetTop) < this.minView) ||
        ((this.current * this.searchListRef.offsetTop) > this.maxView)
    },
    mouseOver(index) {
      this.current = index
      this.minView = this.searchListRef.scrollTop
      this.maxView = this.minView + this.searchListRef.offsetTop * 5
    },
    makeVisible() {
      if ((this.current * this.searchListRef.offsetTop) < this.minView) {
        this.minView = this.minView - this.searchListRef.offsetTop
        this.maxView = this.maxView - this.searchListRef.offsetTop
        this.searchListRef.scrollTop = this.searchListRef.scrollTop - this.searchListRef.offsetTop
      } else {
        this.minView = this.minView + this.searchListRef.offsetTop
        this.maxView = this.maxView + this.searchListRef.offsetTop
        this.searchListRef.scrollTop = this.searchListRef.scrollTop + this.searchListRef.offsetTop
      }
    },
    getMinAndMaxView() {
      this.minView = 0
      if (this.items && this.items.length > 5) {
        this.maxView = 160
      } else if (this.items) {
        this.maxView = this.items.length * 40
      } else {
        this.maxView = 0
      }
    },
    focusOnElement() {
      if (!this.onlyGetExamId) {
        document.getElementById('mainScroll').scrollTop = 182
      }
    }
  },
  created() {
    this.placeholderCustom = this.placeholder
  }
}
</script>

<style lang="scss">
.search {
  .search-container {
    &__icon-search {
      position: absolute;
      top: 8px;
      left: 0;
      color: #7d7d7d;
      min-width: 20px;
      width: 20px;
    }

    .search {
      &__input {
        padding: 0 35px 0 30px;
        height: 100%;
        font-size: 15px;
        border-bottom: 1px solid #9f9f9f;
        color: #303030;
        letter-spacing: -0.2px;

        &::placeholder,
        &::-webkit-input-placeholder {
          font-size: 15px;
          letter-spacing: -0.2px;
          color: #303030;
          /* Opacity for Firefox > 19 */
          opacity: 1;
        }

        &:focus::placeholder,
        &:focus::-webkit-input-placeholder {
          color: #303030;
          /* Opacity for Firefox > 19 */
          opacity: 1;
        }
      }

      &__loading {
        position: absolute;
        right: 10px;
        top: 50%;
        margin-top: -1em;
      }

      &__results-container {
        z-index: 10;
        position: absolute;
        width: 100%;
        min-height: 0;
        max-height: 200px;
        top: 40px;
        background-color: #fff;
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex: 1;
        flex-direction: column;
      }

      &__result-item {
        height: auto;
        border-bottom: 1px solid #eaeaea;
        display: flex;
        align-items: center;
        cursor: pointer;

        &:focus,
        &.focused,
        &.active {
          background-color: #dddddd;
        }
      }

      &__result-text {
        overflow: hidden;
        display: flex;
        flex-direction: column;
        width: 88%;
        height: 100%;
        padding: 20px 0 20px 30px;

        &__highlight {
          color: #7f1a54;
        }
      }

      &__result-details {
        cursor: pointer;

        &__icon {
          &:not(.md-icon-delete) {
            width: 18px;
            height: 18px;
            min-width: 18px;
            min-height: 18px;
            margin: 0 10px 0 0;
            color: #bebebe;

            g {
              fill: #bebebe;
              fill-opacity: 1;
            }
          }
        }

        &__anchor {
          display: flex;
          align-items: center;
          font-weight: 500;
          font-size: 12px;
          color: #bebebe;
          padding: 0 10px;

          &__text {
            border-bottom: 1px solid transparent;
            height: 18px;
          }

          &:hover {
            .search__result-details__anchor__text {
              border-bottom: 1px solid #bebebe;
            }
          }
        }
      }
    }
  }
}
</style>
